/**
 * Enhanced Registration Service
 * Frontend service for manufacturer registration workflow
 */

import { api } from '../lib/axios';

// Types
export interface Address {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
}

export interface CompanyDetails {
  registeredName: string;
  entityType: 'PROPRIETORSHIP' | 'PARTNERSHIP' | 'LLP' | 'PRIVATE_LIMITED' | 'PUBLIC_LIMITED';
  dateOfIncorporation: Date;
  countryOfIncorporation: string;
  registeredAddress: Address;
  manufacturingAddress?: Address;
  facilityType?: 'OWNED' | 'LEASED' | 'CONTRACT_MANUFACTURING';
}

export interface TaxInformation {
  gstin: string;
  pan: string;
  cin?: string;
  udyamNumber?: string;
  gstinValidationStatus: ValidationStatus;
  panValidationStatus: ValidationStatus;
  cinValidationStatus?: ValidationStatus;
}

export interface AuthorizedSignatory {
  fullName: string;
  designation: string;
  email: string;
  mobileNumber: string;
  aadhaarMasked?: string;
  passportNumber?: string;
}

export interface BankingDetails {
  bankName: string;
  accountHolderName: string;
  accountNumber: string;
  ifscCode: string;
  gstApplicability: 'CGST_SGST' | 'IGST';
  preferredCurrency: string;
  billingAddress?: Address;
}

export interface BrandInformation {
  brandNames: string[];
  trademarkStatus: 'REGISTERED' | 'APPLIED' | 'NOT_REGISTERED';
  trademarkNumber?: string;
  industry: string;
  productCategories: string[];
  productSkus?: string[];
  monthlyProductionVolume?: number;
}

export interface ComplianceDeclarations {
  declarationsAccepted: boolean;
  termsAccepted: boolean;
  privacyPolicyAccepted: boolean;
  dataProcessingConsent: boolean;
}

export interface RegistrationSession {
  id: string;
  manufacturerId: string;
  currentStep: RegistrationStep;
  completedSteps: RegistrationStep[];
  data: {
    companyDetails?: CompanyDetails;
    taxInformation?: TaxInformation;
    authorizedSignatory?: AuthorizedSignatory;
    bankingDetails?: BankingDetails;
    brandInformation?: BrandInformation;
    declarations?: ComplianceDeclarations;
  };
  status: RegistrationSessionStatus;
  createdAt: Date;
  updatedAt: Date;
  submittedAt?: Date;
}

export interface ValidationResult {
  success: boolean;
  status: ValidationStatus;
  data?: any;
  error?: string;
}

export type RegistrationStep = 
  | 'company_details' 
  | 'tax_information' 
  | 'authorized_signatory' 
  | 'banking_details' 
  | 'brand_information' 
  | 'declarations' 
  | 'review_submit';

export type RegistrationSessionStatus = 
  | 'IN_PROGRESS' 
  | 'SUBMITTED' 
  | 'UNDER_REVIEW' 
  | 'APPROVED' 
  | 'REJECTED';

export type ValidationStatus = 
  | 'pending' 
  | 'verified' 
  | 'failed' 
  | 'manual_review_required';

class RegistrationService {

  /**
   * Get registration session
   */
  async getRegistrationSession(): Promise<RegistrationSession> {
    try {
      const response = await api.get('/registration/session');
      return response.data.session;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get registration session');
    }
  }

  /**
   * Get registration progress
   */
  async getRegistrationProgress(): Promise<{
    currentStep: RegistrationStep;
    completedSteps: RegistrationStep[];
    totalSteps: number;
    completionPercentage: number;
  }> {
    try {
      const response = await api.get('/registration/progress');
      return response.data.progress;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get registration progress');
    }
  }

  /**
   * Update registration step
   */
  async updateRegistrationStep(step: RegistrationStep, data: any): Promise<void> {
    try {
      await api.post('/registration/step', { step, data });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update registration step');
    }
  }

  /**
   * Submit registration for review
   */
  async submitRegistration(): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.post('/registration/submit', {});
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit registration'
      };
    }
  }

  /**
   * Check registration status
   */
  async checkRegistrationStatus(): Promise<{
    shouldRedirect: boolean;
    registrationStatus: string;
    manufacturer: any;
  }> {
    try {
      const response = await api.get('/registration/status');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to check registration status');
    }
  }

  /**
   * Validate GSTIN
   */
  async validateGSTIN(gstin: string): Promise<ValidationResult> {
    try {
      const response = await api.post('/registration/validate/gstin', { gstin });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        status: 'failed',
        error: error.response?.data?.error || 'GSTIN validation failed'
      };
    }
  }

  /**
   * Validate PAN
   */
  async validatePAN(pan: string, gstin?: string): Promise<ValidationResult> {
    try {
      const response = await api.post('/registration/validate/pan', { pan, gstin });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        status: 'failed',
        error: error.response?.data?.error || 'PAN validation failed'
      };
    }
  }

  /**
   * Validate CIN
   */
  async validateCIN(cin: string): Promise<ValidationResult> {
    try {
      const response = await api.post('/registration/validate/cin', { cin });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        status: 'failed',
        error: error.response?.data?.error || 'CIN validation failed'
      };
    }
  }

  /**
   * Validate IFSC
   */
  async validateIFSC(ifsc: string): Promise<ValidationResult> {
    try {
      const response = await api.post('/registration/validate/ifsc', { ifsc });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        status: 'failed',
        error: error.response?.data?.error || 'IFSC validation failed'
      };
    }
  }

  /**
   * Get validation results
   */
  async getValidationResults(): Promise<any[]> {
    try {
      const response = await api.get('/registration/validation-results');
      return response.data.results;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to get validation results');
    }
  }
}

export const registrationService = new RegistrationService();